<template>
  <div class="prod-page">
    <section class="prod-hero" aria-labelledby="prod-hero-title">
      <LazyBg class="prod-hero-bg" :src="heroBg" eager />
      <div class="prod-hero-inner container">
        <h1 id="prod-hero-title" class="prod-hero-title">AI赋能千行百业</h1>
        <p class="prod-hero-sub">全场景智能解决方案</p>
      </div>
    </section>

    <section class="prod-main">
      <div class="prod-grid container container--grid">
        <article v-for="item in solutionCards" :key="item.id" class="prod-card" data-aos="fade-up">
          <div class="prod-card-media">
            <img v-if="item.image" :src="item.image" :alt="item.title" loading="lazy" width="512" height="300" />
            <div v-else class="prod-card-media-placeholder" aria-hidden="true" />
          </div>

          <div class="prod-card-body">
            <h2 class="prod-card-title">{{ item.title }}</h2>
            <ul v-if="item.lines.length" class="prod-card-list" role="list">
              <li v-for="(line, idx) in item.lines" :key="idx" class="prod-line">
                <span class="prod-dot" aria-hidden="true" />
                <div class="prod-line-inner">
                  <span class="prod-v">{{ line }}</span>
                </div>
              </li>
            </ul>
            <div class="prod-actions">
              <button type="button" class="prod-btn prod-btn-secondary" @click="openConsult(item.phone)">
                咨询采购 &nbsp; {{ item.phone }}
              </button>
            </div>
          </div>
        </article>

        <p v-if="!solutionCards.length" class="prod-empty">暂无解决方案</p>
      </div>
    </section>

    <Teleport to="body">
      <div v-if="consultOpen" class="prod-modal-root" role="presentation">
        <div class="prod-modal-backdrop" aria-hidden="true" @click="consultOpen = false" />
        <div class="prod-modal" role="dialog" aria-modal="true" aria-labelledby="prod-modal-title" tabindex="-1">
          <h2 id="prod-modal-title" class="prod-modal-title">咨询采购</h2>
          <p class="prod-modal-desc">商务热线（工作日 9:30–18:30）</p>
          <a class="prod-modal-tel" :href="`tel:${consultTelDigits}`">{{ consultTelDisplay }}</a>
          <button type="button" class="prod-modal-close" @click="consultOpen = false">关闭</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import heroBg from '@/assets/jjff.png';
import LazyBg from '@/components/LazyBg.vue';
import { computed, onUnmounted, ref, watch } from 'vue';
  import { useSeo } from '@/composables/useSeo';
import { useFrontCms } from '@/composables/useFrontCms';
import { mapSolutionsToCards, type SolutionCardView } from '@/utils/frontCmsMappers';

const CONSULT_TEL = '400-001-5020';
const CONSULT_TEL_DIGITS = '4000015020';

const { solutions } = useFrontCms('solutions');

const FALLBACK_SOLUTIONS: SolutionCardView[] = [
  {
    id: 'medical',
    title: '智慧医疗解决方案',
    image:
      'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80',
    lines: [
      'AI 分诊导诊、AI 辅助问诊筛查、AI 生成式出院小结与病程摘要、影像辅助阅片与病历质控等。',
      '提升诊断与处置效率，减轻医护文书与重复劳动负担，强化过程合规与可追溯管理。',
      '综合医院、医联体与专科联盟、体检中心及区域健康信息平台建设方。'
    ],
    phone: CONSULT_TEL
  },
  {
    id: 'mental-youth',
    title: '青少年心理健康筛查解决方案',
    image:
      'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=800&q=80',
    lines: [
      '校园心理普查、风险分级预警、干预过程记录与随访、家校协同沟通材料辅助生成。',
      '多模态智能评估结合量表与行为线索，早发现、早介入；全流程隐私保护与权限分级。',
      '教育主管部门、K12 与高校、心理服务机构及青少年关爱类公益与社区项目。'
    ],
    phone: CONSULT_TEL
  },
  {
    id: 'knowledge',
    title: '海曦智能知识库管理',
    image:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    lines: [
      '企业文档统一入库、语义检索与问答、随问随写式知识沉淀、跨部门协作与版本留痕。',
      '缩短信息查找时间，降低重复咨询成本，支撑合规审计与知识资产持续运营。',
      '中大型政企、研发与交付团队、客服与运营中心、需要强知识复用的专业服务机构。'
    ],
    phone: CONSULT_TEL
  }
];

const solutionCards = computed(() => {
  const fromApi = mapSolutionsToCards(solutions.value);
  return fromApi.length ? fromApi : FALLBACK_SOLUTIONS;
});

const consultOpen = ref(false);
const consultTelDisplay = ref(CONSULT_TEL);
const consultTelDigits = ref(CONSULT_TEL_DIGITS);

const openConsult = (phone?: string) => {
  if (phone) {
    consultTelDisplay.value = phone;
    consultTelDigits.value = phone.replace(/\D/g, '') || CONSULT_TEL_DIGITS;
  } else {
    consultTelDisplay.value = CONSULT_TEL;
    consultTelDigits.value = CONSULT_TEL_DIGITS;
  }
  consultOpen.value = true;
};

let escOff: (() => void) | null = null;
watch(consultOpen, (open) => {
  escOff?.();
  escOff = null;
  if (!open) return;
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') consultOpen.value = false;
  };
  document.addEventListener('keydown', onKey);
  escOff = () => document.removeEventListener('keydown', onKey);
});
onUnmounted(() => escOff?.());

useSeo({
  title: '解决方案',
  description: '海曦解决方案：智慧医疗、青少年心理筛查、智能知识库等全场景 AI 赋能方案。'
});
</script>

<style scoped lang="scss">
.prod-hero {
  position: relative;
  min-height: 300px;
  display: flex;
  align-items: center;
  padding: 104px 0 52px;
  overflow: hidden;
  color: #ffffff;
}

.prod-hero-bg {
  /* LazyBg */
}

.prod-hero-inner {
  position: relative;
  z-index: 1;
  margin: 0 auto;
}

.prod-hero-title {
  margin: 0 0 14px;
  font-size: var(--fluid-h1, 52px);
  font-weight: 500;
  letter-spacing: 4.16px;
}

.prod-hero-sub {
  margin: 0;
  font-size: var(--fluid-sub, 36px);
  letter-spacing: 4.32px;
}

.prod-main {
  padding: 48px 0 80px;
  background: linear-gradient(180deg, #eef2f7 0%, #f7f8fa 35%, #ffffff 100%);
}

.prod-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px 32px;
}

.prod-empty {
  grid-column: 1 / -1;
  margin: 0;
  padding: 48px;
  text-align: center;
  font-size: 15px;
  color: #8c8c8c;
}

.prod-card {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  padding: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(0, 149, 255, 0.2);
    box-shadow: 0 20px 48px rgba(15, 23, 42, 0.12);

    .prod-card-media img {
      transform: scale(1.05);
    }

    .prod-card-title {
      color: #0095ff;
    }
  }
}

.prod-card-media {
  aspect-ratio: 16 / 9;
  background: #e5e7eb;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    object-fit: cover;
    transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.prod-card-media-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%);
}

.prod-card-body {
  padding: 22px 0 0;
}

.prod-card-title {
  margin: 0 0 18px;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  color: #333;
  transition: color 0.25s ease;
}

.prod-card-list {
  margin: 0 0 22px;
  padding: 0;
  list-style: none;
}

.prod-line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 14px;
  line-height: 1.7;
  color: #4b5563;

  &:last-child {
    margin-bottom: 0;
  }
}

.prod-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin-top: 8px;
  background: #333;
  border-radius: 1px;
}

.prod-line-inner {
  min-width: 0;
}

.prod-v {
  font-size: 14px;
  line-height: 22px;
}

.prod-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.prod-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 4px;
  color: #333;
  background: #f5f5f5;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition:
    background 0.25s ease,
    color 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.prod-btn-secondary {
  &:hover {
    background: #0095ff;
    color: #fff;
    box-shadow: 0 8px 20px rgba(0, 149, 255, 0.28);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .prod-card,
  .prod-card-media img,
  .prod-btn {
    transition: none;
  }

  .prod-card:hover {
    transform: none;
  }

  .prod-card:hover .prod-card-media img {
    transform: none;
  }

  .prod-btn-secondary:hover {
    transform: none;
  }
}

.prod-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.prod-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
}

.prod-modal {
  position: relative;
  width: min(400px, 100%);
  padding: 28px 24px 22px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.prod-modal-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.prod-modal-desc {
  margin: 0 0 16px;
  font-size: 14px;
  color: #6b7280;
}

.prod-modal-tel {
  display: inline-block;
  font-size: 26px;
  font-weight: 700;
  color: #0ea5e9;
  text-decoration: none;
  letter-spacing: 0.04em;
  margin-bottom: 20px;

  &:hover {
    text-decoration: underline;
  }
}

.prod-modal-close {
  display: block;
  width: 100%;
  margin: 0;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #f3f4f6;
  }
}

@media (max-width: 820px) {
  .prod-grid {
    grid-template-columns: 1fr;
  }
}
</style>
